import React, { useState, useEffect } from 'react';
import '../css/InteractivePanels.css'; // Apni standard CSS file import karein

const SHEET_API_URL = "https://opensheet.elk.sh/1y7xU04hGyUohLxPHXtbWfFdgej5eUhCdfMZ3FQHWDo8/Products";

function PanelCard({ panel }) {
  // Split comma-separated images from Google Sheet
  const images = panel.imageUrl ? panel.imageUrl.split(',').map(img => img.trim()) : [];
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  
  // 🛠️ TWO SEPARATE TOGGLE STATES
  const [showAllDesc, setShowAllDesc] = useState(false);
  const [showAllSpecs, setShowAllSpecs] = useState(false);

  // Description truncation logic (120 characters limit)
  const fullDescription = panel.description || "";
  const shouldTruncateDesc = fullDescription.length > 120;
  const visibleDescription = (shouldTruncateDesc && !showAllDesc) 
    ? `${fullDescription.substring(0, 120)}...` 
    : fullDescription;

  // Features split logic
  const featuresArray = panel.features ? panel.features.split(',').map(f => f.trim()) : [];
  const visibleFeatures = showAllSpecs ? featuresArray : featuresArray.slice(0, 3);

  return (
    <div className="panel-card">
      
      {/* Gallery Section */}
      <div className="card-gallery">
        <div className="main-image-wrapper">
          {images.length > 0 ? (
            <img 
              src={images[activeImageIndex]} 
              alt={panel.title} 
              className="main-image"
            />
          ) : (
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>No image found</div>
          )}
          <span className="category-badge">{panel.category || 'General'}</span>
        </div>

        {/* Thumbnails Navigation */}
        {images.length > 1 && (
          <div className="thumbnail-list">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setActiveImageIndex(idx)}
                className={`thumb-btn ${activeImageIndex === idx ? 'active' : ''}`}
              >
                <img src={img} alt="thumbnail" className="thumb-img" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="card-content">
        <div className="card-body-scroll">
          <h3 className="panel-title">{panel.title}</h3>
          
          {panel.sizes && (
            <div className="sizes-wrapper">
              {panel.sizes.split(',').map((size) => (
                <span key={size} className="size-badge">
                  {size.trim()}
                </span>
              ))}
            </div>
          )}

          {/* 🛠️ 1. DESCRIPTION WITH SEE MORE OPTION */}
          <p className="panel-desc" style={{ marginBottom: '12px', lineHeight: '1.5' }}>
            {visibleDescription}
            {shouldTruncateDesc && (
              <span 
                onClick={() => setShowAllDesc(!showAllDesc)}
                style={{
                  color: '#2e7d32',
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginLeft: '6px',
                  fontSize: '13px'
                }}
              >
                {showAllDesc ? ' Read Less ↑' : ' Read More ↓'}
              </span>
            )}
          </p>

          {/* 🛠️ 2. TECHNICAL SPECS WITH SEE MORE OPTION */}
          {featuresArray.length > 0 && (
            <div className="specs-container" style={{ marginTop: '16px' }}>
              <h4 className="specs-heading">Technical Specs (GeM Compliant)</h4>
              <ul className="specs-list">
                {visibleFeatures.map((feature, idx) => (
                  <li key={idx} className="spec-item">
                    <span className="spec-check">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              
              {featuresArray.length > 3 && (
                <button 
                  className="see-more-btn"
                  onClick={() => setShowAllSpecs(!showAllSpecs)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2e7d32',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '4px 0',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '8px'
                  }}
                >
                  {showAllSpecs ? 'See Less Specs ↑' : `See More Specs (${featuresArray.length - 3}+) ↓`}
                </button>
              )}
            </div>
          )}
        </div>

        <button className="cta-button">
          Request Pricing & Specs
        </button>
      </div>

    </div>
  );
}

export default function InteractivePanelsPage() {
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    async function fetchPanelData() {
      try {
        const response = await fetch(SHEET_API_URL);
        const data = await response.json();
        setPanels(data);
        setLoading(false);
      } catch (error) {
        console.error("Error loading panel data:", error);
        setLoading(false);
      }
    }
    fetchPanelData();
  }, []);

  const categories = ['All', ...new Set(panels.map(p => p.category).filter(Boolean))];
  const filteredPanels = selectedCategory === 'All' ? panels : panels.filter(p => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="loading-box">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="panels-container">
      {/* Hero Head */}
      <section className="hero-section">
        <div className="hero-content">
          <span className="hero-subtitle">eVridya</span>
          <h1 className="hero-title">Interactive Flat Panels with Built-in CPU</h1>
          <p className="hero-desc">Explore high-performance multi-touch displays. Change views below to inspect front panels, ports config, and side dimensions.</p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="filter-section">
        <div className="filter-tabs">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`filter-btn ${selectedCategory === cat ? 'active' : ''}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid Elements */}
      <section className="grid-section">
        <div className="panels-grid">
          {filteredPanels.map((panel) => (
            <PanelCard key={panel['Product-id'] || panel.id} panel={panel} />
          ))}
        </div>
      </section>
    </div>
  );
}